/**
 * `/recruiting/candidates` — the Candidates screen, WP-W2.
 * `web/shapes/candidates.md` is the agreed shape, written before this file.
 *
 * The second recruiting surface, and it copies the Jobs pattern (a company-scoped
 * cursor list, a create form, a detail) and adds the two things the candidate domain
 * has: a **right-to-erasure redaction**, and a **CV document** sub-surface whose
 * download is the one sensitive, access-logged read of PII on the recruiting side.
 *
 * ── Sensitive only at the download ──────────────────────────────────────────
 *
 * The list, the detail and the document *metadata* carry no `x-wp-sensitive-access`, so
 * nothing here sends an `X-Access-Reason` for them — collecting a reason the server
 * neither requires nor stores would be under false pretences. Only `GET
 * …/documents/{docId}/download` is sensitive, so the reason is asked **per download**,
 * in the documents panel, not as a gate over the whole screen.
 *
 * ── What each refusal does ──────────────────────────────────────────────────
 *
 * Nothing is applied optimistically. A read refused renders `ApiErrorNote` in its
 * region and leaves the others standing; a write refused renders the server's own
 * sentence and leaves the form's input in place. A redaction re-reads both the detail
 * (it now renders redacted) and the list (a redacted candidate leaves the browse).
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { useSession } from '../../auth/SessionProvider';
import type {
  CandidateDocumentType,
  RecruitingCandidate,
  RecruitingCandidateCreateRequest,
  RecruitingCandidateDocument,
  RecruitingCandidateDocumentListResponse,
  RecruitingCandidateListResponse,
  RecruitingCandidateUpdateRequest,
  CandidateSource,
} from '../../api/recruiting';
import {
  CANDIDATE_SOURCES,
  RECRUITING_CANDIDATES_PATH,
  recruitingCandidateDocumentsPath,
  recruitingCandidatePath,
  recruitingCandidatesPath,
} from '../../api/recruiting';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  deliverBlobDownload,
  downloadCandidateDocument,
  isRedacted,
  registerAndUploadDocument,
  SOURCE_LABEL,
  type DeliverBlob,
  type DownloadState,
  type UploadState,
} from '../../recruiting/candidate';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { SEVERITY_ICON } from '../../states/icons';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { CreateCandidateForm } from './CreateCandidateForm';
import { CandidateEditForm } from './CandidateEditForm';
import { CandidateDocumentsPanel } from './CandidateDocumentsPanel';

const PAGE_LIMIT = 50;
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export interface CandidatesScreenProps {
  /**
   * How a fetched CV is handed to the operator. Injected so a test can drive the
   * download without `URL.createObjectURL`, which jsdom does not implement; production
   * uses the real object-URL-and-anchor delivery.
   */
  readonly deliverDocument?: DeliverBlob;
}

export function CandidatesScreen({
  deliverDocument = deliverBlobDownload,
}: CandidatesScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { user, client } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [sourceFilter, setSourceFilter] = useState<'' | CandidateSource>('');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [listToken, setListToken] = useState(0);
  const [detailToken, setDetailToken] = useState(0);
  const [docsToken, setDocsToken] = useState(0);

  const [redactArmed, setRedactArmed] = useState(false);
  const [registerState, setRegisterState] = useState<UploadState>({ status: 'idle' });
  const [downloadState, setDownloadState] = useState<DownloadState>({ status: 'idle' });

  const trimmedQ = q.trim();
  const listPath = recruitingCandidatesPath({
    limit: PAGE_LIMIT,
    source: sourceFilter === '' ? undefined : sourceFilter,
    q: trimmedQ === '' ? undefined : trimmedQ,
  });
  const list = useApiQuery<RecruitingCandidateListResponse>(listPath, listToken);

  const detailPath = selectedId === null ? null : recruitingCandidatePath(selectedId);
  const detail = useApiQuery<RecruitingCandidate>(detailPath, detailToken);

  const docsPath = selectedId === null ? null : recruitingCandidateDocumentsPath(selectedId);
  const docs = useApiQuery<RecruitingCandidateDocumentListResponse>(docsPath, docsToken);

  const createCandidate = useApiMutation<RecruitingCandidateCreateRequest, RecruitingCandidate>(
    RECRUITING_CANDIDATES_PATH,
    'POST',
  );
  // Field edits and the redaction are two different questions about one candidate, so
  // they never share a pending/error state.
  const fieldPatch = useApiMutation<RecruitingCandidateUpdateRequest, RecruitingCandidate>(
    recruitingCandidatePath('unset'),
    'PATCH',
  );
  const redactPatch = useApiMutation<RecruitingCandidateUpdateRequest, RecruitingCandidate>(
    recruitingCandidatePath('unset'),
    'PATCH',
  );

  const rows: readonly RecruitingCandidate[] = useMemo(() => list.data?.data ?? [], [list.data]);
  const hasMore = list.data?.page.has_more ?? false;
  const current = detail.data;

  const settleDetail = (): void => {
    setDetailToken((value) => value + 1);
    setListToken((value) => value + 1);
  };

  const submitCreate = (body: RecruitingCandidateCreateRequest): void => {
    createCandidate
      .submit(body)
      .then(() => {
        setListToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const saveFields = (body: RecruitingCandidateUpdateRequest): void => {
    if (selectedId === null) return;
    fieldPatch
      .submit(body, recruitingCandidatePath(selectedId))
      .then(settleDetail)
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const setConsent = (value: string | null): void => {
    saveFields({ privacy_consent_at: value });
  };

  const runRedact = (): void => {
    if (selectedId === null) return;
    redactPatch
      .submit({ redact: true }, recruitingCandidatePath(selectedId))
      .then(() => {
        setRedactArmed(false);
        settleDetail();
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const openCandidate = (id: string): void => {
    fieldPatch.reset();
    redactPatch.reset();
    setRedactArmed(false);
    setRegisterState({ status: 'idle' });
    setDownloadState({ status: 'idle' });
    setSelectedId(id);
  };

  const registerDocument = (file: File, docType: CandidateDocumentType): void => {
    if (selectedId === null) return;
    setRegisterState({ status: 'working' });
    registerAndUploadDocument(client, selectedId, file, docType)
      .then((next) => {
        setRegisterState(next);
        if (next.status === 'done') setDocsToken((value) => value + 1);
      })
      .catch(() => {
        /* `registerAndUploadDocument` resolves with a `failed` state rather than rejecting. */
      });
  };

  const downloadDocument = (doc: RecruitingCandidateDocument, reason: string): void => {
    if (selectedId === null) return;
    setDownloadState({ status: 'requesting', docId: doc.id });
    downloadCandidateDocument(client, selectedId, doc, reason, deliverDocument)
      .then(setDownloadState)
      .catch(() => {
        /* resolves with a `refused` state rather than rejecting. */
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.candidates')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('recruiting.candidates.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w2-scope-note">
          {t('recruiting.candidates.scopeNote')}
        </p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-source-filter">
            {t('recruiting.candidates.filter.source')}
          </label>
          <select
            className="wp-field__input"
            id="w2-source-filter"
            data-testid="w2-source-filter"
            value={sourceFilter}
            onChange={(event) => {
              const next = event.target.value;
              setSourceFilter(next === '' ? '' : (next as CandidateSource));
              setSelectedId(null);
            }}
          >
            <option value="">{t('filters.all')}</option>
            {CANDIDATE_SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(SOURCE_LABEL[value])}
              </option>
            ))}
          </select>
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-q">
            {t('recruiting.candidates.filter.q')}
          </label>
          <input
            className="wp-field__input"
            id="w2-q"
            data-testid="w2-q"
            type="search"
            value={q}
            autoComplete="off"
            onChange={(event) => {
              setQ(event.target.value);
              setSelectedId(null);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.candidates.filter.qHint')}</p>
        </div>
      </div>

      {list.status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('recruiting.candidates.subjectWord')}
          testId="w2-list-skeleton"
        />
      ) : null}
      {list.status === 'error' && list.error !== null ? (
        <ApiErrorNote
          error={list.error}
          onRetry={list.reload}
          subject={t('recruiting.candidates.subjectWord')}
          testId="w2-list-error"
        />
      ) : null}

      {list.data === null ? null : rows.length === 0 ? (
        <EmptyState
          title={t('recruiting.candidates.empty.title')}
          description={t('recruiting.candidates.empty.body', {
            filter:
              sourceFilter === ''
                ? t('recruiting.candidates.filterAll')
                : t(SOURCE_LABEL[sourceFilter]),
          })}
          testId="w2-list-empty"
        />
      ) : (
        <>
          {hasMore ? (
            <PartialData
              excludes={t('recruiting.candidates.partial.excludes', { shown: rows.length })}
              testId="w2-list-partial"
            />
          ) : null}
          <ul className="wp-rows" data-testid="w2-list">
            {rows.map((candidate) => (
              <li className="wp-row" key={candidate.id} data-testid={`w2-cand-${candidate.id}`}>
                <div className="wp-row__main">
                  <p>
                    {isRedacted(candidate) ? (
                      <>
                        <Badge
                          severity="medium"
                          icon={SEVERITY_ICON.medium}
                          label={t('recruiting.candidates.redacted.badge')}
                          testId={`w2-cand-redacted-${candidate.id}`}
                        />{' '}
                      </>
                    ) : null}
                    <span>{candidate.full_name}</span>
                  </p>
                  <p className="wp-small wp-muted">
                    {t('recruiting.candidates.row.meta', {
                      source:
                        candidate.source === null
                          ? t('recruiting.candidates.source.none')
                          : t(SOURCE_LABEL[candidate.source]),
                      title: candidate.current_title ?? t('recruiting.candidates.detail.none'),
                      location: candidate.location ?? t('recruiting.candidates.detail.none'),
                    })}
                  </p>
                </div>
                <div className="wp-row__figure">
                  <button
                    className="wp-button"
                    type="button"
                    data-testid={`w2-open-${candidate.id}`}
                    onClick={() => {
                      openCandidate(candidate.id);
                    }}
                  >
                    {t('recruiting.candidates.open')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {selectedId === null ? null : (
        <>
          {detail.status === 'loading' ? (
            <Skeleton
              rows={5}
              variant="block"
              label={t('recruiting.candidates.detail.subject')}
              testId="w2-detail-skeleton"
            />
          ) : null}
          {detail.status === 'error' && detail.error !== null ? (
            <ApiErrorNote
              error={detail.error}
              onRetry={detail.reload}
              subject={t('recruiting.candidates.detail.subject')}
              testId="w2-detail-error"
            />
          ) : null}

          {current === null ? null : (
            <section
              className="wp-section"
              aria-labelledby="w2-detail-heading"
              data-testid="w2-detail"
            >
              <h2 className="wp-section__title" id="w2-detail-heading" tabIndex={-1}>
                {current.full_name}
              </h2>
              {isRedacted(current) ? (
                <p>
                  <Badge
                    severity="medium"
                    icon={SEVERITY_ICON.medium}
                    label={t('recruiting.candidates.redacted.badge')}
                    testId="w2-detail-redacted"
                  />
                </p>
              ) : null}

              <dl className="wp-deflist" data-testid="w2-detail-fields">
                <Field label={t('recruiting.candidates.detail.email')} value={current.email} />
                <Field label={t('recruiting.candidates.detail.phone')} value={current.phone} />
                <Field
                  label={t('recruiting.candidates.detail.source')}
                  value={current.source === null ? null : t(SOURCE_LABEL[current.source])}
                />
                <Field
                  label={t('recruiting.candidates.detail.currentTitle')}
                  value={current.current_title}
                />
                <Field
                  label={t('recruiting.candidates.detail.currentCompany')}
                  value={current.current_company}
                />
                <Field
                  label={t('recruiting.candidates.detail.location')}
                  value={current.location}
                />
                <Field
                  label={t('recruiting.candidates.detail.linkedin')}
                  value={current.linkedin_url}
                />
              </dl>

              <p className="wp-small wp-muted" data-testid="w2-detail-consent">
                {current.privacy_consent_at === null
                  ? t('recruiting.candidates.detail.noConsent')
                  : t('recruiting.candidates.detail.consent', {
                      when: formatInstant(current.privacy_consent_at, locale, timezone),
                    })}
              </p>
              {current.redacted_at === null ? null : (
                <p className="wp-small wp-muted" data-testid="w2-detail-redacted-at">
                  {t('recruiting.candidates.redacted.at', {
                    when: formatInstant(current.redacted_at, locale, timezone),
                  })}
                </p>
              )}
              <p className="wp-small wp-muted" data-testid="w2-last-updated">
                {t('recruiting.candidates.lastUpdated', {
                  when: formatInstant(current.updated_at, locale, timezone),
                })}
              </p>

              {isRedacted(current) ? (
                <StatusRedacted note={t('recruiting.candidates.redacted.note')} />
              ) : (
                <>
                  <CandidateEditForm
                    key={`${current.id}:${current.updated_at}`}
                    candidate={current}
                    pending={fieldPatch.status === 'pending'}
                    error={fieldPatch.error}
                    saved={fieldPatch.status === 'ok'}
                    locale={locale}
                    timezone={timezone}
                    onSubmit={saveFields}
                    onConsent={setConsent}
                  />

                  <section
                    className="wp-section"
                    aria-labelledby="w2-redact-heading"
                    data-testid="w2-redact"
                  >
                    <h3 className="wp-section__title" id="w2-redact-heading">
                      {t('recruiting.candidates.redact.title')}
                    </h3>
                    <p className="wp-small" data-testid="w2-redact-warning">
                      {t('recruiting.candidates.redact.warning')}
                    </p>
                    {redactArmed ? (
                      <div className="wp-filters__actions" data-testid="w2-redact-confirm-row">
                        <p className="wp-small">
                          {t('recruiting.candidates.redact.confirmQuestion')}
                        </p>
                        <button
                          className="wp-button wp-button--danger"
                          type="button"
                          data-testid="w2-redact-confirm"
                          disabled={redactPatch.status === 'pending'}
                          onClick={runRedact}
                        >
                          {redactPatch.status === 'pending'
                            ? t('recruiting.candidates.redact.pending')
                            : t('recruiting.candidates.redact.confirm')}
                        </button>
                        <button
                          className="wp-button"
                          type="button"
                          data-testid="w2-redact-cancel"
                          disabled={redactPatch.status === 'pending'}
                          onClick={() => {
                            setRedactArmed(false);
                          }}
                        >
                          {t('recruiting.candidates.redact.cancel')}
                        </button>
                      </div>
                    ) : (
                      <div className="wp-filters__actions">
                        <button
                          className="wp-button wp-button--danger"
                          type="button"
                          data-testid="w2-redact-start"
                          onClick={() => {
                            setRedactArmed(true);
                          }}
                        >
                          {t('recruiting.candidates.redact.start')}
                        </button>
                      </div>
                    )}
                    {redactPatch.error === null ? null : (
                      <ApiErrorNote
                        error={redactPatch.error}
                        subject={t('recruiting.candidates.subjectWord')}
                        testId="w2-redact-error"
                      />
                    )}
                  </section>
                </>
              )}

              <CandidateDocumentsPanel
                status={docs.status}
                documents={docs.data?.data ?? null}
                readError={docs.error}
                onReload={docs.reload}
                locale={locale}
                timezone={timezone}
                registerState={registerState}
                onRegister={registerDocument}
                downloadState={downloadState}
                onDownload={downloadDocument}
              />
            </section>
          )}
        </>
      )}

      {/* The create form is the last section, not the first: a recruiter opens this
          screen to read the list, and opening a new one is the occasional action.
          Leading with the form put the subject of the page below the fold. */}
      <CreateCandidateForm
        pending={createCandidate.status === 'pending'}
        error={createCandidate.error}
        created={createCandidate.status === 'ok' ? createCandidate.data : null}
        onSubmit={submitCreate}
      />
    </div>
  );
}

/** One definition-list row; a null value reads as the em-dash placeholder, never blank. */
function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <dt className="wp-small wp-muted">{label}</dt>
      <dd>{value === null || value === '' ? t('recruiting.candidates.detail.none') : value}</dd>
    </>
  );
}

/** The redacted note, its own component only so the redacted branch reads as one thing. */
function StatusRedacted({ note }: { readonly note: string }): ReactNode {
  return (
    <p className="wp-small wp-muted" data-testid="w2-redacted-note">
      {note}
    </p>
  );
}
